import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react'
import { Trash2, RotateCcw, Save, Plus, ChevronDown, ChevronRight, Eye, EyeOff, ExternalLink } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { TopTabs } from '@/components/dashboard/TopTabs'
import { useVendors } from '@/hooks/useVendors'
import {
  useDeleteVendor,
  usePatchVendor,
  useRestoreVendors,
  type VendorResponse,
  type VendorUpsertRequest,
} from '@/hooks/useVendorMutations'
import { ApiError } from '@/lib/api/rawFetch'
import { AddVendorModal } from '@/components/vendors/AddVendorModal'
import { CountryCombobox } from '@/components/vendors/CountryCombobox'

// Extend VendorResponse with fields the generated.ts hasn't picked up yet
// (b2b_portal_url, b2b_username, b2b_password, duty_payer,
// free_shipping_dollars, country_of_origin) — gen-api regen will eventually
// fold these in.
type DutyPayer = 'Dapper' | 'Vendor' | 'Split' | 'Duty-free'
type Row = VendorResponse & {
  _dirty?: boolean
  b2b_portal_url?: string | null
  b2b_username?: string | null
  b2b_password?: string | null
  duty_payer?: DutyPayer | null
  free_shipping_dollars?: number | null
  country_of_origin?: string | null
}

// "Duty-free" answers "is there duty" rather than "who pays it", but it
// lives in the same dropdown because the buyer asks one question. See the
// matching note in backend/schemas/vendor.py.
const DUTY_PAYER_OPTIONS: DutyPayer[] = ['Dapper', 'Vendor', 'Split', 'Duty-free']

// Number of <th> in the header row — the Details panel spans all of them.
const COLUMN_COUNT = 13

const NUM_KEYS = [
  'lead_time',
  'buffer_days',
  'multiplier',
  'mov_dollars',
  'duty_pct',
  'freight_pct',
] as const

const TEXT_KEYS = [
  'supplier',
  'contact_name',
  'phone',
  'email',
  'payment_terms',
  'notes',
  'b2b_portal_url',
  'b2b_username',
  'b2b_password',
  'duty_payer',
  'country_of_origin',
] as const

// Uses Row (not VendorResponse) so we can index by b2b_portal_url — that
// field isn't in the generated VendorResponse type yet (gen-api not regen'd
// since the column was added) but exists on Row.
function eq(a: Row, b: Row): boolean {
  for (const k of NUM_KEYS) if ((a[k] ?? 0) !== (b[k] ?? 0)) return false
  for (const k of TEXT_KEYS) if ((a[k] ?? '') !== (b[k] ?? '')) return false
  // free_shipping_dollars is compared separately from NUM_KEYS: null ("no
  // free-shipping threshold") and 0 ("free on any order") are different
  // rules, and the `?? 0` coalesce used above would collapse them.
  if ((a.free_shipping_dollars ?? null) !== (b.free_shipping_dollars ?? null)) return false
  return true
}

// Snap decimal fields to hundredths at save-time so the backend never stores
// >2dp values, even if the user typed `1.234` and clicked Save without
// blurring the field first.
const round2 = (n: number) => Math.round(n * 100) / 100

// Show the value rounded to 2dp when the cell is NOT focused (so legacy
// rows storing 1.0467 / 1.180000 read cleanly as 1.05 / 1.18), but expose
// the raw editable string while focused so the user can freely type any
// number of decimals. On blur the entered value is rounded and pushed
// back to the parent; clearing the field falls back to `fallback`.
function DecimalCell({
  value,
  onChange,
  fallback,
  min,
  className,
}: {
  value: number
  onChange: (v: number) => void
  fallback: number
  min?: number
  className?: string
}) {
  const [editing, setEditing] = useState<string | null>(null)
  return (
    <input
      type="number"
      step="0.01"
      min={min}
      value={editing ?? value.toFixed(2)}
      onFocus={() => setEditing(String(round2(value)))}
      onChange={(e) => {
        setEditing(e.target.value)
        const n = Number(e.target.value)
        if (e.target.value !== '' && !isNaN(n)) onChange(n)
      }}
      onBlur={(e) => {
        setEditing(null)
        const n = Number(e.target.value)
        onChange(e.target.value === '' || isNaN(n) ? fallback : round2(n))
      }}
      className={className}
    />
  )
}

// Duty % and Freight % are STORED as decimal fractions (0.05 = 5%). That
// convention is load-bearing — `multiplier` is literally 1 + duty + freight,
// and the dashboard computes cost of order as unit_cost × (1 + duty + freight)
// — so this cell converts at the DISPLAY layer only and never changes what
// goes on the wire.
//
// Precision is 2dp in PERCENT space (= 4dp on the fraction), not the round2
// used elsewhere. Real vendor data carries three decimals (5/A Baker freight
// 0.063 = 6.3%, Acavallo 0.069 = 6.9%); snapping those to 2dp would quietly
// rewrite 6.3% as 6% the first time anything on that row was edited.
const pct4 = (n: number) => Math.round(n * 10000) / 10000

// Fraction -> the percent string shown/edited, with trailing zeros trimmed
// so 0.05 reads "5" not "5.00" and 0.063 reads "6.3".
const toPctStr = (v: number) => String(Math.round(v * 1000000) / 10000)

function PercentCell({
  value,
  onChange,
  className,
}: {
  value: number
  onChange: (v: number) => void
  className?: string
}) {
  const [editing, setEditing] = useState<string | null>(null)
  const isEditing = editing !== null
  return (
    <input
      // Unfocused it's text so the "%" suffix can render; focused it flips to
      // a real number input holding the bare percent, same trick MoneyCell
      // uses for its thousands separators.
      type={isEditing ? 'number' : 'text'}
      step="0.1"
      min={0}
      inputMode="decimal"
      value={isEditing ? editing : `${toPctStr(value)}%`}
      onFocus={() => setEditing(toPctStr(value))}
      onChange={(e) => {
        setEditing(e.target.value)
        const n = Number(e.target.value)
        if (e.target.value !== '' && !isNaN(n)) onChange(pct4(n / 100))
      }}
      onBlur={(e) => {
        setEditing(null)
        const n = Number(e.target.value)
        onChange(e.target.value === '' || isNaN(n) ? 0 : pct4(n / 100))
      }}
      className={className}
    />
  )
}

// MOV is in whole-dollar territory (smallest values are ~$100; most are
// $250–$5,000). Show with thousands commas when unfocused for legibility
// ("1,500" not "1500.00"), but flip to a raw editable number while the user
// is typing — type="number" can't render commas, and we don't want to write
// a full keyboard-handler for digit + comma parsing. The Money formatter uses
// 0 fraction digits since MOV is always whole dollars in the wild.
const MOV_FMT = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

function MoneyCell({
  value,
  onChange,
  fallback,
  min,
  className,
}: {
  value: number
  onChange: (v: number) => void
  fallback: number
  min?: number
  className?: string
}) {
  const [editing, setEditing] = useState<string | null>(null)
  const isEditing = editing !== null
  return (
    <input
      type={isEditing ? 'number' : 'text'}
      step="1"
      min={min}
      inputMode="numeric"
      value={isEditing ? editing : MOV_FMT.format(Math.round(value))}
      onFocus={() => setEditing(String(Math.round(value)))}
      onChange={(e) => {
        setEditing(e.target.value)
        const n = Number(e.target.value)
        if (e.target.value !== '' && !isNaN(n)) onChange(n)
      }}
      onBlur={(e) => {
        setEditing(null)
        const n = Number(e.target.value)
        onChange(e.target.value === '' || isNaN(n) ? fallback : round2(n))
      }}
      className={className}
    />
  )
}

// Free-shipping threshold. Same whole-dollar formatting as MoneyCell, but
// nullable: an empty field means "this vendor has no free-shipping
// threshold" and is stored as NULL, which is NOT the same as $0 ("free on
// any order"). Renders an em-dash placeholder when unset.
function FreeShipCell({
  value,
  onChange,
  className,
}: {
  value: number | null
  onChange: (v: number | null) => void
  className?: string
}) {
  const [editing, setEditing] = useState<string | null>(null)
  const isEditing = editing !== null
  return (
    <input
      type={isEditing ? 'number' : 'text'}
      step="1"
      min={0}
      inputMode="numeric"
      placeholder="—"
      value={isEditing ? editing : value === null ? '' : MOV_FMT.format(Math.round(value))}
      onFocus={() => setEditing(value === null ? '' : String(Math.round(value)))}
      onChange={(e) => {
        setEditing(e.target.value)
        const n = Number(e.target.value)
        if (e.target.value !== '' && !isNaN(n)) onChange(n)
      }}
      onBlur={(e) => {
        setEditing(null)
        const raw = e.target.value.trim()
        if (raw === '') {
          onChange(null)
          return
        }
        const n = Number(raw)
        onChange(isNaN(n) ? null : round2(n))
      }}
      className={className}
    />
  )
}

// Fields in the expanded Details panel get a visible border (unlike the
// borderless grid cells) because they sit on a filled background with no
// column header to identify them — the box is the affordance.
const detailInput =
  'w-full px-1.5 py-1 text-xs border border-[var(--line-input)] bg-[var(--surface-card)] ' +
  // Focus is an affordance (identity), not an attention signal — so burgundy.
  'focus:outline-none focus:border-[var(--brand)]'

function DetailField({
  label,
  className,
  children,
}: {
  label: string
  className?: string
  children: ReactNode
}) {
  return (
    <label className={`flex flex-col gap-1 ${className ?? ''}`}>
      <span className="text-[10px] uppercase tracking-wide text-[var(--mute)]">{label}</span>
      {children}
    </label>
  )
}

// Shared vendor-portal login. Masked by default so it isn't readable over a
// shoulder or in a screen share while someone edits an unrelated field; the
// eye toggles it because the whole point of storing it is reading it back.
// This is display-only masking — the value is plaintext in the DB and in the
// GET /api/vendors/ response. See _migrate_vendor_b2b_password.
function PasswordCell({
  value,
  onChange,
  vendor,
}: {
  value: string
  onChange: (v: string) => void
  vendor: string
}) {
  const [show, setShow] = useState(false)
  return (
    <div className="relative">
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        // Chrome will happily autofill the operator's OWN saved credentials
        // into a bare password input. "new-password" is the reliable opt-out.
        autoComplete="new-password"
        className={`${detailInput} pr-7`}
      />
      <button
        type="button"
        // The field sits inside DetailField's <label>, which would forward
        // this click to the input and steal focus. preventDefault stops that.
        onClick={(e) => {
          e.preventDefault()
          setShow((s) => !s)
        }}
        aria-label={`${show ? 'Hide' : 'Show'} B2B password for ${vendor}`}
        className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 text-[var(--mute)] hover:text-[var(--text-primary)]"
      >
        {show ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
      </button>
    </div>
  )
}

// The portal field is free text, so only http(s) may ever become a link —
// never javascript:, data: or anything else a typo or paste could produce.
// A bare domain gets https:// so the owner needn't type a scheme.
function portalHref(value: string): string | null {
  const v = value.trim()
  if (!v || /\s/.test(v)) return null
  const hasScheme = /^[a-z][a-z0-9+.-]*:/i.test(v)
  if (hasScheme && !/^https?:\/\//i.test(v)) return null
  const href = hasScheme ? v : `https://${v}`
  try {
    const { protocol } = new URL(href)
    return protocol === 'http:' || protocol === 'https:' ? href : null
  } catch {
    return null
  }
}

// Editable like any other detail field, with a one-click open in a new tab
// because the owner visits these portals constantly. Built from the current
// typed value, so an unsaved edit opens what's on screen.
function PortalUrlCell({
  value,
  onChange,
  vendor,
}: {
  value: string
  onChange: (v: string) => void
  vendor: string
}) {
  const href = portalHref(value)
  return (
    <div className="relative">
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`${detailInput} pr-7`}
        placeholder="https://…"
      />
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          // Inside DetailField's <label>; keep the click on the link alone.
          onClick={(e) => e.stopPropagation()}
          aria-label={`Open B2B portal for ${vendor}`}
          className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 text-[var(--mute)] hover:text-[var(--brand)]"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </a>
      ) : (
        <span
          aria-hidden="true"
          title="Enter a portal URL"
          className="absolute right-1 top-1/2 -translate-y-1/2 p-0.5 text-[var(--mute)] opacity-40"
        >
          <ExternalLink className="h-3.5 w-3.5" />
        </span>
      )}
    </div>
  )
}

// Sticky header cells. Applied per-<th> rather than to <thead>, because
// sticky on <thead> only works in newer engines while sticky on <th> is
// universally supported. The background must be opaque or scrolled rows
// show through, and z-10 keeps it above the body's inputs and comboboxes.
// The bottom rule is an inset shadow, not border-b: with the default
// border-collapse the header's own border is liable to be dropped or to
// drift by a pixel while the row scrolls under it.
const thCell =
  'tbl-th sticky top-0 z-10 bg-[var(--surface-header)] ' +
  'shadow-[inset_0_-1px_0_var(--line-warm)]'

// Zebra striping. Applied to the vendor row only — the Details panel gets
// its own tone (--surface-page) so it reads as a nested block rather than as the next
// stripe. A dirty row's amber wins over both, since "unsaved" matters more
// than "even/odd". Index-based (of the visible rows), so a filter re-stripes.
const bandFor = (i: number) =>
  i % 2 === 1 ? 'bg-[var(--surface-zebra)]' : 'bg-[var(--surface-card)]'

// Amber = attention: the row has unsaved changes.
const DIRTY_BG = 'bg-[var(--status-awaiting-bg)]'

function pickPatchBody(r: Row): VendorUpsertRequest {
  return {
    vendor: r.vendor,
    supplier: r.supplier ?? '',
    contact_name: r.contact_name ?? '',
    phone: r.phone ?? '',
    email: r.email ?? '',
    payment_terms: r.payment_terms ?? '',
    lead_time: r.lead_time,
    buffer_days: r.buffer_days,
    mov_dollars: round2(r.mov_dollars),
    // Sent even when null so the backend's fields_set check sees it and can
    // clear the threshold — omitting it would make the field one-way.
    free_shipping_dollars:
      r.free_shipping_dollars == null ? null : round2(r.free_shipping_dollars),
    // pct4, not round2 — these are fractions displayed as percentages, and
    // round2 would send 0.063 (6.3%) to the backend as 0.06 (6%). That was a
    // latent data-loss path even before the percent display: any edit to an
    // unrelated cell on the row patched the whole row through round2.
    duty_pct: pct4(r.duty_pct),
    freight_pct: pct4(r.freight_pct),
    country_of_origin: r.country_of_origin ?? '',
    multiplier: round2(r.multiplier),
    notes: r.notes ?? '',
    b2b_portal_url: r.b2b_portal_url ?? '',
    b2b_username: r.b2b_username ?? '',
    b2b_password: r.b2b_password ?? '',
    duty_payer: r.duty_payer ?? null,
  }
}

// Stable stand-in for `data` while the vendors query is still in flight. An
// inline `= []` default mints a new array on every render, which makes the
// `[serverRows]` effect below re-run and setRows on every render — an endless
// render/effect loop that pegs the CPU until the fetch happens to land (and,
// once the page gets heavy enough, trips React's "Maximum update depth
// exceeded" instead).
const NO_ROWS: VendorResponse[] = []

// Sentinels for the supplier filter. Prefixed so they can never collide with
// a real supplier name; "no supplier" needs its own token because an
// empty-string <option> value is indistinguishable from "nothing selected".
const ALL_SUPPLIERS = '__all__'
const NO_SUPPLIER = '__none__'

export default function Vendors() {
  const { data: serverRows = NO_ROWS, isLoading, refetch } = useVendors()
  const [rows, setRows] = useState<Row[]>([])
  const [removed, setRemoved] = useState<string[]>([])
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [addOpen, setAddOpen] = useState(false)
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  // Display-only. `rows` deliberately stays whole: it backs dirty-tracking and
  // Save, so narrowing it would silently discard a pending edit the moment the
  // filter changed.
  const [supplierFilter, setSupplierFilter] = useState<string>(ALL_SUPPLIERS)

  const patchVendor = usePatchVendor()
  const deleteVendor = useDeleteVendor()
  const restore = useRestoreVendors()

  useEffect(() => {
    setRows(serverRows.map((v) => ({ ...v })))
    setRemoved([])
    setConfirmDelete(false)
  }, [serverRows])

  const serverIndex = useMemo(() => {
    const m = new Map<string, Row>()
    for (const v of serverRows) m.set(v.vendor, v)
    return m
  }, [serverRows])

  const dirtyRows = useMemo(
    () => rows.filter((r) => {
      const s = serverIndex.get(r.vendor)
      return s && !eq(r, s)
    }),
    [rows, serverIndex]
  )

  // Suppliers present on the current rows, plus a bucket for vendors with no
  // supplier set (otherwise they'd be unreachable while filtering). The active
  // selection is kept in the list even if it no longer matches any row — a
  // vendor renamed while filtered would otherwise leave the <select> showing
  // some other supplier's name.
  const supplierOptions = useMemo(() => {
    const names = new Set<string>()
    let anyBlank = false
    for (const r of rows) {
      const s = (r.supplier ?? '').trim()
      if (s) names.add(s)
      else anyBlank = true
    }
    if (supplierFilter !== ALL_SUPPLIERS && supplierFilter !== NO_SUPPLIER) {
      names.add(supplierFilter)
    }
    const sorted = Array.from(names).sort((a, b) => a.localeCompare(b))
    return anyBlank || supplierFilter === NO_SUPPLIER ? [...sorted, NO_SUPPLIER] : sorted
  }, [rows, supplierFilter])

  const visibleRows = useMemo(() => {
    if (supplierFilter === ALL_SUPPLIERS) return rows
    if (supplierFilter === NO_SUPPLIER) return rows.filter((r) => !(r.supplier ?? '').trim())
    return rows.filter((r) => (r.supplier ?? '').trim() === supplierFilter)
  }, [rows, supplierFilter])

  const hasChanges = dirtyRows.length > 0 || removed.length > 0
  const needsConfirm = removed.length > 0 && !confirmDelete

  function updateRow(vendor: string, patch: Partial<Row>) {
    setRows((prev) => prev.map((r) => (r.vendor === vendor ? { ...r, ...patch } : r)))
  }

  function toggleExpanded(vendor: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(vendor)) next.delete(vendor)
      else next.add(vendor)
      return next
    })
  }

  function markRemove(vendor: string) {
    setRemoved((prev) => (prev.includes(vendor) ? prev : [...prev, vendor]))
    setRows((prev) => prev.filter((r) => r.vendor !== vendor))
    setExpanded((prev) => {
      if (!prev.has(vendor)) return prev
      const next = new Set(prev)
      next.delete(vendor)
      return next
    })
  }

  async function save() {
    if (!hasChanges) return
    if (needsConfirm) {
      setConfirmDelete(true)
      return
    }
    let failures = 0
    for (const r of dirtyRows) {
      try {
        await patchVendor.mutateAsync({ vendor: r.vendor, body: pickPatchBody(r) })
      } catch (err) {
        failures++
        const msg = err instanceof Error ? err.message : 'patch failed'
        toast.error(`${r.vendor}: ${msg}`)
      }
    }
    for (const v of removed) {
      try {
        await deleteVendor.mutateAsync(v)
      } catch (err) {
        failures++
        if (err instanceof ApiError && err.status === 409) {
          toast.error(`${v}: cannot delete — has open POs`)
        } else {
          const msg = err instanceof Error ? err.message : 'delete failed'
          toast.error(`${v}: ${msg}`)
        }
      }
    }
    if (failures === 0) toast.success(`Saved ${dirtyRows.length + removed.length} change(s)`)
    refetch()
  }

  async function undo() {
    if (!window.confirm('Restore vendor rules from the last saved backup?')) return
    try {
      const res = await restore.mutateAsync()
      toast.success(`Restored ${res.restored} vendors`)
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'restore failed'
      toast.error(msg)
    }
  }

  return (
    <div className="brand-theme flex flex-col h-screen bg-[var(--surface-page)] text-[var(--text-primary)]">
      <TopTabs />
      {/* Capped so the table doesn't stretch edge-to-edge on a wide monitor.
          Every column except Supplier and Vendor has a fixed width, so all the
          slack on a 2000px screen landed in those two — hundreds of px of empty
          cell. ~1400px is the sum of the fixed widths plus comfortable room for
          the two text columns, so nothing horizontally scrolls at the cap. */}
      {/* w-full + max-w + mx-auto, in that order, is load-bearing: auto inline
          margins on a flex item override align-self:stretch, so without an
          explicit w-full the container would collapse to fit-content and the
          w-full table inside it would size circularly. */}
      {/* This column no longer scrolls — the table wrapper below does, so the
          toolbar stays pinned and the sticky header has a scroll container it
          actually lives inside. min-h-0 is required: a flex child defaults to
          min-height:auto and would refuse to shrink below its content, so the
          inner overflow-auto would never engage. */}
      <div className="flex-1 min-h-0 flex flex-col p-4 w-full max-w-[1400px] mx-auto">
        <div className="flex items-center gap-2 mb-3">
          <h1 className="text-base font-semibold">Vendor Rules</h1>
          <span className="text-xs text-[var(--mute)]">
            {supplierFilter === ALL_SUPPLIERS
              ? `${rows.length} vendors`
              : `${visibleRows.length} of ${rows.length} vendors`}
          </span>

          {/* Native <select>, matching the duty-payer control and every other
              input on this page. */}
          <select
            value={supplierFilter}
            onChange={(e) => setSupplierFilter(e.target.value)}
            aria-label="Filter by supplier"
            className={`h-7 max-w-[200px] border px-1 text-xs bg-transparent ${
              supplierFilter === ALL_SUPPLIERS
                ? 'border-[var(--line-input)] text-[var(--mute)]'
                : 'border-[var(--text-primary)] text-[var(--text-primary)]'
            }`}
          >
            <option value={ALL_SUPPLIERS}>All suppliers</option>
            {supplierOptions.map((s) => (
              <option key={s} value={s}>
                {s === NO_SUPPLIER ? 'No supplier' : s}
              </option>
            ))}
          </select>
          <div className="flex-1" />
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setAddOpen(true)} data-testid="vendors-add">
            <Plus className="mr-1 h-3 w-3" />
            Add Vendor
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={undo} disabled={restore.isPending}>
            <RotateCcw className="mr-1 h-3 w-3" />
            Undo Last Save
          </Button>
          <Button
            size="sm"
            className="h-7 text-xs"
            disabled={!hasChanges || patchVendor.isPending || deleteVendor.isPending}
            onClick={save}
            data-testid="vendors-save"
          >
            <Save className="mr-1 h-3 w-3" />
            {needsConfirm ? `Confirm + Save (${removed.length} removed)` : `Save${hasChanges ? ` (${dirtyRows.length + removed.length})` : ''}`}
          </Button>
          {needsConfirm && (
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => {
              setRemoved([])
              setConfirmDelete(false)
              refetch()
            }}>Cancel</Button>
          )}
        </div>

        {needsConfirm && (
          <div className="mb-3 p-2 border border-[var(--line-warm)] bg-[var(--status-awaiting-bg)] text-xs text-[var(--status-awaiting-fg)]">
            Removing {removed.length} vendor(s): {removed.join(', ')}. Vendors with open POs will fail with a 409.
          </div>
        )}

        {isLoading ? (
          <div className="space-y-1">
            {Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-8 w-full" />)}
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto border border-[var(--line-warm)] bg-[var(--surface-card)]">
            <table className="text-xs w-full">
              <thead>
                <tr className="text-[var(--mute)]">
                  <th className={`${thCell} text-left`}>Details</th>
                  <th className={`${thCell} text-left`}>Supplier</th>
                  <th className={`${thCell} text-left`}>Vendor</th>
                  <th className={`${thCell} text-right`}>Lead Time</th>
                  <th className={`${thCell} text-right`}>Buffer</th>
                  <th className={`${thCell} text-right`}>Multiplier</th>
                  <th className={`${thCell} text-right`}>MOV</th>
                  <th className={`${thCell} text-right`}>Free Shipping</th>
                  <th className={`${thCell} text-center`}>Duty Payer</th>
                  <th className={`${thCell} text-right`}>Duty %</th>
                  <th className={`${thCell} text-right`}>Freight %</th>
                  <th className={`${thCell} text-left`}>Shipping Country</th>
                  <th className={thCell} />
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r, i) => {
                  const dirty = serverIndex.get(r.vendor) && !eq(r, serverIndex.get(r.vendor)!)
                  const cellInput = "w-full px-1 py-0.5 border border-transparent hover:border-[var(--line-input)] bg-transparent focus:outline-none focus:border-[var(--brand)]"
                  const numInput = `${cellInput} text-right tabular-nums`
                  const isOpen = expanded.has(r.vendor)
                  return (
                    <Fragment key={r.vendor}>
                    <tr className={`border-b border-[var(--line-warm)] ${dirty ? DIRTY_BG : bandFor(i)}`}>
                      <td className="px-1 py-0.5 w-8">
                        <button
                          onClick={() => toggleExpanded(r.vendor)}
                          aria-expanded={isOpen}
                          aria-label={`${isOpen ? 'Hide' : 'Show'} details for ${r.vendor}`}
                          className="p-1 text-[var(--mute)] hover:text-[var(--text-primary)]"
                        >
                          {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                        </button>
                      </td>
                      <td className="px-1 py-0.5">
                        <input value={r.supplier ?? ''} onChange={(e) => updateRow(r.vendor, { supplier: e.target.value })} className={cellInput} />
                      </td>
                      <td className="px-2 py-1 text-[var(--text-primary)] font-medium whitespace-nowrap">{r.vendor}</td>
                      <td className="px-1 py-0.5 w-16">
                        <input type="number" min={1} value={r.lead_time} onChange={(e) => updateRow(r.vendor, { lead_time: Number(e.target.value) || 1 })} className={numInput} />
                      </td>
                      <td className="px-1 py-0.5 w-16">
                        <input type="number" min={0} value={r.buffer_days} onChange={(e) => updateRow(r.vendor, { buffer_days: Number(e.target.value) || 0 })} className={numInput} />
                      </td>
                      <td className="px-1 py-0.5 w-20">
                        <DecimalCell value={r.multiplier} onChange={(v) => updateRow(r.vendor, { multiplier: v })} fallback={1} className={numInput} />
                      </td>
                      <td className="px-1 py-0.5 w-24">
                        <MoneyCell value={r.mov_dollars} onChange={(v) => updateRow(r.vendor, { mov_dollars: v })} fallback={0} min={0} className={numInput} />
                      </td>
                      <td className="px-1 py-0.5 w-24">
                        <FreeShipCell
                          value={r.free_shipping_dollars ?? null}
                          onChange={(v) => updateRow(r.vendor, { free_shipping_dollars: v })}
                          className={numInput}
                        />
                      </td>
                      <td className="px-1 py-0.5 w-24">
                        <select
                          value={r.duty_payer ?? ''}
                          onChange={(e) =>
                            updateRow(r.vendor, {
                              duty_payer: e.target.value === '' ? null : (e.target.value as DutyPayer),
                            })
                          }
                          className={`${cellInput} text-center`}
                          aria-label={`Duty payer for ${r.vendor}`}
                        >
                          <option value="">—</option>
                          {DUTY_PAYER_OPTIONS.map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      </td>
                      <td className="px-1 py-0.5 w-16">
                        <PercentCell value={r.duty_pct} onChange={(v) => updateRow(r.vendor, { duty_pct: v })} className={numInput} />
                      </td>
                      <td className="px-1 py-0.5 w-16">
                        <PercentCell value={r.freight_pct} onChange={(v) => updateRow(r.vendor, { freight_pct: v })} className={numInput} />
                      </td>
                      <td className="px-1 py-0.5 w-40">
                        <CountryCombobox
                          value={r.country_of_origin ?? null}
                          onChange={(v) => updateRow(r.vendor, { country_of_origin: v })}
                          vendor={r.vendor}
                          className={`${cellInput} text-left`}
                        />
                      </td>
                      <td className="px-1 py-0.5 w-8">
                        <button aria-label={`Remove ${r.vendor}`} onClick={() => markRemove(r.vendor)} className="p-1 hover:text-destructive">
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className={`border-b border-[var(--line-warm)] ${dirty ? DIRTY_BG : 'bg-[var(--surface-page)]'}`}>
                        <td colSpan={COLUMN_COUNT} className="px-6 py-3">
                          <div className="grid grid-cols-2 gap-x-6 gap-y-2 max-w-3xl mx-auto lg:grid-cols-4">
                            <DetailField label="Contact">
                              <input value={r.contact_name ?? ''} onChange={(e) => updateRow(r.vendor, { contact_name: e.target.value })} className={detailInput} />
                            </DetailField>
                            <DetailField label="Phone">
                              <input value={r.phone ?? ''} onChange={(e) => updateRow(r.vendor, { phone: e.target.value })} className={detailInput} />
                            </DetailField>
                            <DetailField label="Email">
                              <input type="email" value={r.email ?? ''} onChange={(e) => updateRow(r.vendor, { email: e.target.value })} className={detailInput} />
                            </DetailField>
                            <DetailField label="Payment Method">
                              <input value={r.payment_terms ?? ''} onChange={(e) => updateRow(r.vendor, { payment_terms: e.target.value })} className={detailInput} />
                            </DetailField>
                            <DetailField label="B2B Portal" className="col-span-2 lg:col-span-4">
                              <PortalUrlCell
                                value={r.b2b_portal_url ?? ''}
                                onChange={(v) => updateRow(r.vendor, { b2b_portal_url: v })}
                                vendor={r.vendor}
                              />
                            </DetailField>
                            <DetailField label="B2B Username" className="col-span-2">
                              <input
                                value={r.b2b_username ?? ''}
                                onChange={(e) => updateRow(r.vendor, { b2b_username: e.target.value })}
                                className={detailInput}
                              />
                            </DetailField>
                            <DetailField label="B2B Password" className="col-span-2">
                              <PasswordCell
                                value={r.b2b_password ?? ''}
                                onChange={(v) => updateRow(r.vendor, { b2b_password: v })}
                                vendor={r.vendor}
                              />
                            </DetailField>
                            <DetailField label="Notes" className="col-span-2 lg:col-span-4">
                              <textarea
                                value={r.notes ?? ''}
                                onChange={(e) => updateRow(r.vendor, { notes: e.target.value })}
                                rows={2}
                                className={`${detailInput} resize-y min-h-[40px]`}
                              />
                            </DetailField>
                          </div>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <AddVendorModal open={addOpen} onOpenChange={setAddOpen} />
    </div>
  )
}
